import { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { TOOLTIP_DELAY } from 'constants/params';
import { TooltipPosition } from './tooltip';
import { setActiveTooltip, clearActiveTooltip } from './tooltip-lock';

const TOOLTIP_GAP = 16; // px

export const useTooltip = (
  targetEl: React.MutableRefObject<HTMLElement | null>,
  tipEl: React.MutableRefObject<HTMLDivElement | null>,
  position: TooltipPosition,
  dynamicPosition?: boolean
) => {
  const longPressActive = useRef(false);
  const timeoutId = useRef<ReturnType<typeof setTimeout>>();
  const [actualPosition, setActualPosition] = useState<TooltipPosition>(position);
  const [visible, setVisible] = useState(false);

  const show = useCallback(() => {
    clearTimeout(timeoutId.current);
    timeoutId.current = setTimeout(() => setVisible(true), TOOLTIP_DELAY);
  }, []);

  const hide = useCallback(() => {
    clearTimeout(timeoutId.current);
    setVisible(false);
  }, []);

  const controller = useMemo(() => ({ hide, show }), [hide, show]);

  const onEnter = () => {
    if (!tipEl.current || !targetEl.current) return;
    const style = tipEl.current.style;
    const rect = targetEl.current.getBoundingClientRect();
    style.left = `${rect.left}px`;
    style.top = `${rect.top}px`;
    style.width = `${rect.width}px`;
    style.height = `${rect.height}px`;
    longPressActive.current = true;
    setActiveTooltip(controller, targetEl.current);

    if (!dynamicPosition) return;
    const tipRect = tipEl.current.querySelector('div')!.getBoundingClientRect();
    const newPosition = getTooltipPosition(rect, tipRect, position);
    setActualPosition(newPosition);
  };

  const onLeave = (e?: React.MouseEvent) => {
    clearActiveTooltip(controller, e?.relatedTarget ?? null);
  };

  const onClickCapture = (e: MouseEvent | React.MouseEvent) => {
    if (!longPressActive.current) return;
    longPressActive.current = false;
    e.preventDefault();
    e.stopPropagation();
  };

  useEffect(() => {
    if (!visible) return;
    window.addEventListener('wheel', hide);
    return () => {
      window.removeEventListener('wheel', hide);
    };
  }, [visible]);

  return { onEnter, onLeave, onClickCapture, visible, actualPosition };
};

/* H E L P E R S */

const getTooltipPosition = (
  triggerRect: DOMRect,
  tooltipRect: DOMRect,
  preferred: TooltipPosition
): TooltipPosition => {
  const spacing = TOOLTIP_GAP;

  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  const fitsTop =
    triggerRect.top >= tooltipRect.height + spacing &&
    triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2 >= 0 &&
    triggerRect.left + triggerRect.width / 2 + tooltipRect.width / 2 <= viewportWidth;

  const fitsBottom =
    viewportHeight - triggerRect.bottom >= tooltipRect.height + spacing &&
    triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2 >= 0 &&
    triggerRect.left + triggerRect.width / 2 + tooltipRect.width / 2 <= viewportWidth;

  const fitsLeft =
    triggerRect.left >= tooltipRect.width + spacing &&
    triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2 >= 0 &&
    triggerRect.top + triggerRect.height / 2 + tooltipRect.height / 2 <= viewportHeight;

  const fitsRight =
    viewportWidth - triggerRect.right >= tooltipRect.width + spacing &&
    triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2 >= 0 &&
    triggerRect.top + triggerRect.height / 2 + tooltipRect.height / 2 <= viewportHeight;

  const positions: Record<TooltipPosition, boolean> = {
    top: fitsTop,
    bottom: fitsBottom,
    left: fitsLeft,
    right: fitsRight,
  };

  if (positions[preferred]) return preferred;

  const fallback = (['top', 'bottom', 'left', 'right'] as TooltipPosition[]).find((pos) => positions[pos]);

  return fallback || preferred;
};
